/**
 * useSimpleProfileMint — the one path from a guest to a Simple profile.
 *
 * Extracted from AnonConnectionsListPage (signup E3a, Step 2) so the old
 * "⚡ Connect" and the new guest `Done` share the retry / slow / failed
 * handling instead of growing two copies of it:
 *   - A failed mint is said on screen with Try again, never a dead button
 *     (guest-session-loss, Mark 2026-10-07) — `mintFailed` drives the card.
 *   - Slow: after GUEST_SAVE_SLOW_MS the card shows and the mint keeps
 *     waiting; if it lands after all, the card clears and `onMinted` runs.
 *   - The first mint to land wins, once — a Try again can overlap a slow
 *     first attempt (`landedRef`).
 *
 * `onMinted(profileUserId, seedRoom)` is the caller's onward move (the old
 * page opens the QR; the new flow lands on new People). Funnel step 2
 * (`logFunnelQRCreated`) fires here, before it, for both callers.
 *
 * @param {object} opts
 * @param {string|null} opts.sessionChatRoomId - logged with every error; tried first as the seed
 * @param {string[]} [opts.rooms] - further candidate seed rooms, in order
 * @param {(profileUserId: string, seedRoom: string) => void} opts.onMinted
 */
import { useState, useRef } from 'react';
import { GUEST_SAVE_SLOW_MS } from '../services/chat/chatService';
import { mintSimpleProfile, findMintSeedRoom } from '../services/auth/simpleProfile';
import {
  logFunnelQRCreated,
  logConnectionError,
  logEvent,
} from '../services/analytics/eventLogger';
import { ensureSignedIn } from '../services/supabase';

export const useSimpleProfileMint = ({ sessionChatRoomId, rooms = [], onMinted }) => {
  const [minting, setMinting] = useState(false);
  const [mintFailed, setMintFailed] = useState(false);
  const landedRef = useRef(false);

  const runMint = async () => {
    setMinting(true);
    let slow = null;
    try {
      const candidateRooms = [...(sessionChatRoomId ? [sessionChatRoomId] : []), ...rooms];
      const seedRoom = await findMintSeedRoom(candidateRooms);
      if (!seedRoom) {
        // Queryable, never silent (device pass 2: a dead button with the
        // reason locked in the device console)
        console.warn('[ANON_PEOPLE] Get your QR: no share-back record found');
        logConnectionError('sp_mint_no_seed', {
          chatRoomId: sessionChatRoomId,
          message: `no share-back record among ${candidateRooms.length} rooms`,
        });
        return;
      }
      slow = setTimeout(() => {
        logConnectionError('sp_mint_slow', {
          chatRoomId: sessionChatRoomId,
          message: `mint still running after ${GUEST_SAVE_SLOW_MS / 1000}s`,
        });
        setMintFailed(true);
      }, GUEST_SAVE_SLOW_MS);
      const { profileUserId } = await mintSimpleProfile(seedRoom);
      setMintFailed(false);
      if (landedRef.current) return;
      landedRef.current = true;
      // Funnel step 2: receive → sharer (the propagation seed rate reads 1→2)
      logFunnelQRCreated(profileUserId, seedRoom);
      // The tab still holds the host's code link the guest came in on; a
      // profile holder opening that link scans it, so a later reload of this
      // tab replayed it as a fresh scan (device pass, 2026-10-21). Back to
      // the app's home, replacing the entry so Back doesn't return to it.
      if (window.location.pathname.startsWith('/u/')) {
        window.history.replaceState({}, '', '/');
      }
      onMinted(profileUserId, seedRoom);
    } catch (err) {
      if (landedRef.current) return;
      console.error('[ANON_PEOPLE] Simple-profile mint failed:', err);
      logConnectionError('sp_mint_failed', {
        chatRoomId: sessionChatRoomId,
        message: err?.message || 'mint failed',
      });
      setMintFailed(true);
    } finally {
      clearTimeout(slow);
      setMinting(false);
    }
  };

  const mint = async () => {
    if (minting) return;
    await runMint();
  };

  // Try again: sign in again first (put back a vanished sign-in, else a
  // fresh one), then the mint itself — a failure brings the card back
  const retryMint = async () => {
    logEvent('guest_save_retry', { surface: 'mint' });
    setMintFailed(false);
    await ensureSignedIn();
    await runMint();
  };

  return { minting, mintFailed, mint, retryMint };
};
